import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';
import { InvoicesBlock, IStudent, OrphanInvoicesBlock } from '../parts';

interface IInvoicesTabProps {
  student: IStudent;
  onChanged?: () => void;
}

/**
 * Per-student issued invoices view (TZ §4.1 / Phase 13).
 *
 * Layout:
 *   - One section per non-terminated agreement of the student, each with
 *     KPI strip + invoices table filtered by `agreementId` (provided by
 *     the shared `InvoicesBlock`).
 *   - A fallback «Без договора» section listing invoices that still hang
 *     off the student via the legacy `linkedStudent` FK without ever
 *     getting a `payment`. Provided by `OrphanInvoicesBlock`.
 */
export const InvoicesTab = memo(({ student, onChanged }: IInvoicesTabProps) => {
  const { t } = useTranslation();

  const activeAgreements = useMemo(
    () =>
      (student.agreements || []).filter(
        (a): a is NonNullable<typeof a> => Boolean(a) && !a!.terminated,
      ),
    [student.agreements],
  );

  return (
    <div style={{ marginTop: 16 }}>
      {activeAgreements.length === 0 ? (
        <S.EmptyState>
          {t('pages.studentDetail.invoices.noAgreements')}
        </S.EmptyState>
      ) : (
        activeAgreements.map((a) => (
          <S.Card key={a.id}>
            <S.CardTitle>
              {t('pages.studentDetail.invoices.byAgreement', {
                title: a.agreementTitle,
              })}
              {a.group?.name ? ` · ${a.group.name}` : ''}
            </S.CardTitle>
            <InvoicesBlock agreement={a} onChanged={onChanged} />
          </S.Card>
        ))
      )}

      <OrphanInvoicesBlock studentId={student.id} onChanged={onChanged} />
    </div>
  );
});
